import { alpha, createTheme } from '@mui/material';
import { darken } from '@mui/system';

import {
  primaryBlue,
  primaryGrey,
  darkBlue,
  primaryOrange,
  infoBlue,
  infoBackground,
  white,
  primaryRed,
  successGreen,
  lightGrey,
  secondaryGrey,
  darkBlue2,
  lightGrey2,
  primaryPurple,
  lightPurple,
  yellow,
  primaryGreen,
} from './colors';
import { medium } from './fontWeights';

declare module '@mui/material/styles' {
  interface Palette {
    purple: Palette['primary'];
    yellow: Palette['primary'];
    green: Palette['primary'];
    lightPurple: Palette['primary'];
    dark: Palette['primary'];
  }
  interface PaletteOptions {
    purple: PaletteOptions['primary'];
    yellow: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
    lightPurple: PaletteOptions['primary'];
    dark: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    purple: true;
    yellow: true;
    green: true;
    lightPurple: true;
    dark: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    purple: true;
    yellow: true;
    green: true;
    lightPurple: true;
    dark: true;
  }
}

declare module '@mui/system' {
  interface Shape {
    customBorderRadius: {
      small: number;
      medium: number;
      large: number;
    };
  }
}

export default createTheme({
  spacing: 5,
  shape: {
    borderRadius: 1,
    customBorderRadius: {
      small: 5,
      medium: 8,
      large: 12,
    },
  },
  palette: {
    text: {
      primary: primaryGrey,
      secondary: secondaryGrey,
    },
    warning: {
      main: primaryOrange,
      dark: darken(primaryOrange, 0.2),
      contrastText: white,
    },
    primary: {
      main: primaryBlue,
      dark: darken(primaryBlue, 0.2),
      contrastText: white,
    },
    info: {
      main: secondaryGrey,
      dark: darken(secondaryGrey, 0.2),
      contrastText: white,
    },
    secondary: {
      main: darkBlue,
      light: darkBlue2,
      dark: darken(darkBlue, 0.2),
      contrastText: white,
    },
    error: {
      main: primaryRed,
      dark: darken(primaryRed, 0.2),
      contrastText: white,
    },
    success: {
      main: successGreen,
      dark: darken(successGreen, 0.2),
      contrastText: white,
    },
    purple: {
      main: primaryPurple,
      dark: darken(primaryPurple, 0.3),
      contrastText: white,
    },
    yellow: {
      main: yellow,
      dark: darken(yellow, 0.2),
      contrastText: white,
    },
    green: {
      main: primaryGreen,
      dark: darken(primaryGreen, 0.2),
      contrastText: white,
    },
    lightPurple: {
      main: lightPurple,
      dark: darken(lightPurple, 0.2),
      contrastText: white,
    },
    dark: {
      main: primaryGrey,
      dark: darken(primaryGrey, 0.3),
      contrastText: white,
    },
  },
  typography: {
    fontFamily: [
      'Urbanist',
      '-apple-system',
      'BlinkMacSystemFont',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '38px',

      '@media (max-width: 910px)': {
        fontSize: '30px',
      },

      '@media (max-width: 780px)': {
        fontSize: '27px',
      },

      '@media (max-width: 350px)': {
        fontSize: '23px',
      },
    },
    h2: {
      fontSize: '32px',

      '@media (max-width: 780px)': {
        fontSize: '24px',
      },

      '@media (max-width: 350px)': {
        fontSize: '21px',
      },
    },
    h3: {
      fontSize: '25px',
      fontWeight: medium,

      '@media (max-width: 780px)': {
        fontSize: '22px',
      },

      '@media (max-width: 350px)': {
        fontSize: '20px',
      },
    },
    h4: {
      fontSize: '20px',
      fontWeight: medium,

      '@media (max-width: 780px)': {
        fontSize: '18px',
      },

      '@media (max-width: 350px)': {
        fontSize: '16px',
      },
    },
    h5: {
      fontSize: '20px',
      fontWeight: medium,

      '@media (max-width: 780px)': {
        fontSize: '18px',
      },

      '@media (max-width: 350px)': {
        fontSize: '16px',
      },
    },
    h6: {
      fontSize: '16px',
      fontWeight: medium,
    },
    subtitle1: {
      fontSize: '18px',
    },
    subtitle2: {
      fontSize: '14px',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontSize: '1rem',
          borderRadius: 5,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          borderRadius: 5,
        },
        standardInfo: {
          backgroundColor: infoBackground,
          color: infoBlue,
          '& .MuiAlert-icon': {
            color: infoBlue,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    //@ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          borderRadius: 5,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '20px',
          textAlign: 'center',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: primaryGrey,
          backgroundColor: lightGrey,
          fontSize: '14px',
        },
        arrow: {
          color: lightGrey,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: lightGrey,
          color: lightGrey2,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#000', 0.65),
        },
        paper: {
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderRadius: 5,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              borderRadius: 5,
            },
          },
        },
      },
    },

  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 700,
      md: 923,
      lg: 1070,
      xl: 1200,
    },
  },
});
